import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Box as MuiBox, FormControl as MuiFormControl } from "@mui/material";
import { Autocomplete } from "app/shared/ui/Autocomplete/Autocomplete";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { format, isValid as isDateValid, parseISO } from "date-fns";
import { ResetFilter } from "app/shared/ui/FilterPanel/ResetFilter";
import { STORAGE_TYPE, STORAGE_NAMES } from "app/shared/constants";
/** Services */
import { useSelectedSubmission } from "app/services/selectedSubmissionService";
import { getGrantsFilterOptionsValue } from "app/services/grantsService";
import { useGrantsContext } from "app/services/grantsContext";
import { useBrowserStorage } from "app/services/StorageService/StorageHelper";
/** Context and Component to show the error on UI */
import { useAlerts } from "common";

const initialGrantFilterValues = {
  title: "",
  grantNumber: "",
  projectStartDateMin: "",
  projectStartDateMax: "",
  projectEndDateMin: "",
  projectEndDateMax: "",
  principal: "",
  grantRole: "",
};

const grantFilterStyles = {
  fieldTextStyle: {
    margin: "inherit",
    color: "#666666",
  },
  datePickerWrapper: {
    display: "flex",
    "& .MuiInputBase-formControl": {
      width: "auto",
    },
    "& .MuiInputBase-input": {
      padding: "0px 3px",
    },
    "& .MuiInputBase-root": {
      padding: "8px",
      fontSize: "14px",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "21px",
    },
    "& .MuiIconButton-root": {
      margin: 0,
      padding: "8px 0px",
    },
    "&&": {
      "& .MuiFormControl-root": {
        marginTop: "5px",
        marginBottom: "5px",
        width: "100%",
      },
    },
  },
  dateSeparator: {
    margin: "14px 5px",
  },
};

export const GrantFilter = () => {
  const { t } = useTranslation();
  const { saveItem } = useBrowserStorage(STORAGE_TYPE.SESSION);

  const navigate = useNavigate();

  const { grantsPreference, setGrantsPreference } = useGrantsContext();

  let initialFilterValues = grantsPreference.grants.filters;

  // Controllable States - grantStartDateFrom
  const [projectStartDateMin, setProjectStartDateMin] = useState(
    initialFilterValues.projectStartDateMin
      ? parseISO(initialFilterValues.projectStartDateMin)
      : null,
  );

  // Controllable States - grantStartDateTo
  const [projectStartDateMax, setProjectStartDateMax] = useState(
    initialFilterValues.projectStartDateMax
      ? parseISO(initialFilterValues.projectStartDateMax)
      : null,
  );

  // Controllable States - grantEndDateFrom
  const [projectEndDateMin, setProjectEndDateMin] = useState(
    initialFilterValues.projectEndDateMin
      ? parseISO(initialFilterValues.projectEndDateMin)
      : null,
  );

  // Controllable States - grantEndDateTo
  const [projectEndDateMax, setProjectEndDateMax] = useState(
    initialFilterValues.projectEndDateMax
      ? parseISO(initialFilterValues.projectEndDateMax)
      : null,
  );

  const [getSelectedSubmission] = useSelectedSubmission().value;
  const [getSelectedSubmissionName] = useSelectedSubmission().name;
  const [titleList, setTitleList] = useState([]);
  const [awardList, setAwardList] = useState([]);
  const [projectPeriodList, setProjectPeriodList] = useState([]);
  const [maxDateStart, setMaxDateStart] = useState(null);
  const [minDateStart, setMinDateStart] = useState(null);
  const [maxDateEnd, setMaxDateEnd] = useState(null);
  const [minDateEnd, setMinDateEnd] = useState(null);
  const [startDateFromError, setStartDateFromError] = useState(false);
  const [startDateToError, setStartDateToError] = useState(false);
  const [endDateFromError, setEndDateFromError] = useState(false);
  const [endDateToError, setEndDateToError] = useState(false);
  const [grantFiltersSelectedValues, setGrantFiltersSelectedValues] = useState({
    ...initialFilterValues,
  });
  const { changeFilterData, submissionUpdate } = useSelectedSubmission();

  const { setAlert, clearAlert } = useAlerts();
  const selectedSubmission = getSelectedSubmission();

  /** API call to getAll grantFilterOptions */
  useEffect(() => {
    getGrantsFilterOptionsValue(
      setTitleList,
      setAwardList,
      setProjectPeriodList,
      selectedSubmission,
      navigate,
      setAlert,
      clearAlert,
      getSelectedSubmissionName,
      t,
    );
    // eslint-disable-next-line
  }, [selectedSubmission, navigate]);

  //project Period
  if (Object.keys(projectPeriodList).length && !minDateEnd && !minDateStart) {
    setMinDateEnd(parseISO(projectPeriodList.projectEndDateMin));
    setMinDateStart(parseISO(projectPeriodList.projectStartDateMin));
    setMaxDateStart(parseISO(projectPeriodList.projectStartDateMax));
    setMaxDateEnd(parseISO(projectPeriodList.projectEndDateMax));
  }

  const resetFn = () => {
    saveItem(
      STORAGE_NAMES.TGDS_GRANTS_DASHBOARD_FILTERS,
      initialGrantFilterValues,
    );
    setGrantFiltersSelectedValues({ ...initialGrantFilterValues });
    setProjectStartDateMin(null);
    setProjectEndDateMin(null);
    setProjectStartDateMax(null);
    setProjectEndDateMax(null);
    setFilterPreference({ ...initialGrantFilterValues });
    changeFilterData(initialGrantFilterValues);
  };

  // *** Helper Function to Update Context Object, on Field Value Update ***
  const setFilterPreference = (fieldUpdate) => {
    setGrantsPreference({
      ...grantsPreference,
      grants: {
        ...grantsPreference.grants,
        filters: {
          ...grantsPreference.grants.filters,
          ...fieldUpdate,
        },
        sort: {
          orderBy: "",
          order: "",
        },
        pagination: {
          page: "",
          pageSize: "",
        },
      },
    });
  };

  /**  On Dropdown Value change, update search results **/
  const handleFilterValueUpdate = (fieldName, fieldValue, updateFilterData) => {
    const updatedFilterValues = {
      ...grantFiltersSelectedValues,
      [fieldName]: fieldValue,
    };

    if (updateFilterData) {
      setFilterPreference(updatedFilterValues);
      setGrantFiltersSelectedValues(updatedFilterValues);
    }
  };

  /* On Updates to the Submission Id, refresh the Search Results and Filter Panel*/
  useEffect(() => {
    if (submissionUpdate) {
      resetFn();
    }

    // eslint-disable-next-line
  }, [submissionUpdate]);

  const grantFilterLabel = "grant.mainView.filters";
  return (
    <>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="titleAutoComplete"
          key="title-autocomplete"
          clientSide
          options={titleList}
          getOptionLabel={(option) => option?.title ?? ""}
          value={grantFiltersSelectedValues.title}
          label={t(`${grantFilterLabel}.title`)}
          onChange={(e, value) => {
            handleFilterValueUpdate("title", value || "", true);
          }}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="awardNumberAutoComplete"
          key="award-number-autocomplete"
          clientSide
          options={awardList}
          getOptionLabel={(option) => option}
          value={grantFiltersSelectedValues.grantNumber}
          label={t(`${grantFilterLabel}.awardNumber`)}
          onChange={(e, value) => {
            handleFilterValueUpdate("grantNumber", value || "", true);
          }}
        />
      </MuiFormControl>
      <MuiFormControl>
        <label style={grantFilterStyles.fieldTextStyle}>
          {t(`${grantFilterLabel}.projectStartDate`)}
        </label>
        <MuiBox sx={grantFilterStyles.datePickerWrapper}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              maxDate={
                projectStartDateMax || maxDateStart || new Date("01/01/2100")
              }
              minDate={minDateStart || new Date("01/01/1970")}
              invalidDateMessage={t(`${grantFilterLabel}.invalidDateFormat`)}
              margin="normal"
              id="date-picker-inline-start-from"
              value={projectStartDateMin}
              onChange={(value) => {
                setProjectStartDateMin(value);
                handleFilterValueUpdate(
                  "projectStartDateMin",
                  (isDateValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setStartDateFromError(
                    t("trainee.edit.traineeForm.maxDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setStartDateFromError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setStartDateFromError(null);
                } else {
                  setStartDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(startDateFromError),
                  helperText: startDateFromError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
            <MuiBox component="span" sx={grantFilterStyles.dateSeparator}>
              {t(`${grantFilterLabel}.dateSeparator`)}
            </MuiBox>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              minDate={
                projectStartDateMin || minDateStart || new Date("01/01/1970")
              }
              maxDate={maxDateEnd || maxDateStart || new Date("01/01/2100")}
              invalidDateMessage={t(`${grantFilterLabel}.invalidDateFormat`)}
              margin="normal"
              id="date-picker-inline-start-to"
              value={projectStartDateMax}
              onChange={(value) => {
                setProjectStartDateMax(value);
                handleFilterValueUpdate(
                  "projectStartDateMax",
                  (isDateValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setStartDateToError(
                    t("trainee.edit.traineeForm.minDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setStartDateToError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setStartDateToError(null);
                } else {
                  setStartDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(startDateToError),
                  helperText: startDateToError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
          </MuiLocalizationProvider>
        </MuiBox>
      </MuiFormControl>
      <MuiFormControl>
        <label style={grantFilterStyles.fieldTextStyle}>
          {t(`${grantFilterLabel}.projectEndDate`)}
        </label>
        <MuiBox sx={grantFilterStyles.datePickerWrapper}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              maxDate={
                projectEndDateMax || maxDateStart || new Date("01/01/2100")
              }
              minDate={minDateStart || new Date("01/01/1970")}
              invalidDateMessage={t(`${grantFilterLabel}.invalidDateFormat`)}
              margin="normal"
              id="date-picker-inline-end-from"
              value={projectEndDateMin}
              onChange={(value) => {
                setProjectEndDateMin(value);
                handleFilterValueUpdate(
                  "projectEndDateMin",
                  (isDateValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setEndDateFromError(
                    t("trainee.edit.traineeForm.maxDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setEndDateFromError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setEndDateFromError(null);
                } else {
                  setEndDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(endDateFromError),
                  helperText: endDateFromError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
            <MuiBox component="span" sx={grantFilterStyles.dateSeparator}>
              {t(`${grantFilterLabel}.dateSeparator`)}
            </MuiBox>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              minDate={
                projectEndDateMin || minDateStart || new Date("01/01/1970")
              }
              maxDate={maxDateEnd || maxDateStart || new Date("01/01/2100")}
              invalidDateMessage={t(`${grantFilterLabel}.invalidDateFormat`)}
              margin="normal"
              id="date-picker-inline-end-to"
              value={projectEndDateMax}
              onChange={(value) => {
                setProjectEndDateMax(value);
                handleFilterValueUpdate(
                  "projectEndDateMax",
                  (isDateValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setEndDateToError(
                    t("trainee.edit.traineeForm.minDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setEndDateToError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setEndDateToError(null);
                } else {
                  setEndDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(endDateToError),
                  helperText: endDateToError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
          </MuiLocalizationProvider>
        </MuiBox>
      </MuiFormControl>
      <ResetFilter resetFn={resetFn} />
    </>
  );
};
